import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../contexts/AuthContext';
import TemplateSelector from '../components/templates/TemplateSelector';

const CreateBoard = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'tasks',
    columns: 'Backlog, To Do, In Progress, Review, Done',
    workspaceId: ''
  });
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingTeams, setFetchingTeams] = useState(true);
  const [error, setError] = useState(null);
  const [creationMode, setCreationMode] = useState('scratch'); // 'scratch' or 'template'
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const res = await axios.get('/api/workspaces', {
          headers: { 'x-auth-token': token }
        });
        setWorkspaces(res.data);
        if (res.data.length > 0) {
          setFormData(prev => ({ ...prev, workspaceId: res.data[0]._id }));
        }
      } catch (err) {
        console.error('Error fetching teams', err);
      } finally {
        setFetchingTeams(false);
      }
    };
    fetchWorkspaces();
  }, [token]);

  const { name, description, type, columns, workspaceId } = formData;

  const onChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectTemplate = (template) => {
    // Pre-fill form fields from template
    // Note: Templates are for cards, so we just use this as a starting point
    setFormData(prev => ({
      ...prev,
      name: prev.name || template.name,
      description: prev.description || template.descriptionTemplate
    }));
    setShowTemplateSelector(false);
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (!workspaceId) {
      setError('Please select a team first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columnsArray = columns.split(',').map(col => col.trim()).filter(col => col !== '');

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      const body = { name, description, type, columns: columnsArray, workspaceId };
      const res = await axios.post('/api/boards', body, config);

      navigate(`/boards/${res.data.slug || res.data._id}`);
    } catch (err) {
      setError(err.response?.data?.msg || 'Error creating board');
      setLoading(false);
    }
  };

  if (fetchingTeams) return <div className="p-8 text-center text-gray-500">Loading your teams...</div>;

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-8 border border-gray-100 dark:border-gray-700">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Create New Board</h1>

        {error && (
          <div className="bg-red-100 dark:bg-red-950/40 border border-red-400 dark:border-red-900 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {workspaces.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-300 mb-4">You need to be part of a Team to create a board.</p>
            <Link to="/teams" className="px-6 py-2 bg-primary-blue text-white rounded-md hover:bg-blue-700 font-bold">
              Go to Teams
            </Link>
          </div>
        ) : (
          <>
            {showTemplateSelector ? (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select a Template</h2>
                  <button
                    type="button"
                    onClick={() => setShowTemplateSelector(false)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-250 text-sm"
                  >
                    Back
                  </button>
                </div>
                <TemplateSelector
                  onSelectTemplate={handleSelectTemplate}
                  onCancel={() => setShowTemplateSelector(false)}
                />
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-3">
                    How would you like to create your board?
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setCreationMode('scratch')}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        creationMode === 'scratch'
                          ? 'border-primary-blue bg-blue-50 dark:bg-blue-950/20'
                          : 'border-gray-200 dark:border-gray-750 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white">Create from Scratch</div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Configure columns and settings</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCreationMode('template');
                        setShowTemplateSelector(true);
                      }}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        creationMode === 'template'
                          ? 'border-primary-blue bg-blue-50 dark:bg-blue-950/20'
                          : 'border-gray-200 dark:border-gray-750 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="font-semibold text-gray-900 dark:text-white">Use Template</div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Start from a preset template</p>
                    </button>
                  </div>
                </div>

                <form onSubmit={onSubmit}>
                  <div className="mb-6">
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                      Team (Workspace) *
                    </label>
                    <select
                      name="workspaceId"
                      value={workspaceId}
                      onChange={onChange}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-605 rounded-lg focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    >
                      {workspaces.map(ws => (
                        <option key={ws._id} value={ws._id}>{ws.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                      Board Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={onChange}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-605 rounded-lg focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="e.g. Website Redesign"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={description}
                      onChange={onChange}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-605 rounded-lg focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      placeholder="Briefly describe the purpose of this board"
                      rows="3"
                    />
                  </div>

                  <div className="mb-8">
                    <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                      Initial Columns
                    </label>
                    <input
                      type="text"
                      name="columns"
                      value={columns}
                      onChange={onChange}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-605 rounded-lg focus:ring-primary-blue focus:border-primary-blue bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-gray-400 dark:text-gray-500 text-[10px] mt-2 italic">
                      Separate column names with commas.
                    </p>
                  </div>

                  <div className="flex items-center justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="text-gray-500 dark:text-gray-400 hover:underline text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-primary-blue hover:bg-blue-700 text-white font-bold py-2.5 px-8 rounded-lg transition-all shadow-md disabled:opacity-50"
                    >
                      {loading ? 'Creating...' : 'Create Board'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreateBoard;
